import {
  Center,
  Checkmark,
  Listbox,
  ListboxRootProps,
  Spinner,
  useListboxItemContext,
  useSlotRecipe,
} from '@chakra-ui/react';
import { CSSProperties, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

function ListBoxItemCheckmark() {
  const recipe = useSlotRecipe({ key: 'listbox' });
  const { itemIndicator } = recipe();
  const itemState = useListboxItemContext();

  return <Checkmark css={itemIndicator} filled size="sm" checked={itemState.selected} disabled={itemState.disabled} />;
}

type ListBoxOption = { label: string; value: string };

type RowData<T extends ListBoxOption> = {
  items: T[];
};

function ListboxRow<T extends ListBoxOption>({
  index,
  style,
  data,
}: Readonly<{ index: number; style: CSSProperties; data: RowData<T> }>) {
  const item = data.items[index];

  return (
    <div style={style}>
      <Listbox.Item item={item}>
        <ListBoxItemCheckmark />
        <Listbox.ItemText>{item.label}</Listbox.ItemText>
      </Listbox.Item>
    </div>
  );
}

interface ListboxWithCheckmarkProps extends ListboxRootProps {
  label?: string;
  isLoading?: boolean;
}

export default function ListboxWithCheckmark({
  label,
  isLoading,
  collection,
  onValueChange,
  ...props
}: Readonly<ListboxWithCheckmarkProps>) {
  const itemData = useMemo(
    () => ({
      items: collection.items,
    }),
    [collection.items],
  );

  return (
    <Listbox.Root collection={collection} selectionMode="multiple" onValueChange={onValueChange} {...props}>
      {label && <Listbox.Label>{label}</Listbox.Label>}
      <Listbox.Content display="flex" flex={1} minHeight={0}>
        {isLoading ? (
          <Center height="48px" width="100%">
            <Spinner boxSize="20px" color="neutral.white" />
          </Center>
        ) : (
          <AutoSizer style={{ width: '100%', height: '100%', position: 'relative' }} disableWidth>
            {({ height }) =>
              collection.items.length === 0 ? (
                <Listbox.Empty>
                  <FormattedMessage id="components.listbox.defaultEmptyMsg" />
                </Listbox.Empty>
              ) : (
                <FixedSizeList
                  className="cockpit-scrollbar-900 no-separator"
                  width="100%"
                  height={height}
                  itemCount={collection.items.length}
                  itemSize={56}
                  itemData={itemData}
                  itemKey={(index, data) => data.items[index].value}
                >
                  {ListboxRow}
                </FixedSizeList>
              )
            }
          </AutoSizer>
        )}
      </Listbox.Content>
    </Listbox.Root>
  );
}
